/**
 * Panel uncertainty breakdown. v1: expected vs actual per factor,
 * R_eff = R0 · (actual_disp / expected_disp) · [actual_vig / expected_vig when 2w].
 * v2 (quote.uncertainty_detail): calibrated P_panel factors — see PanelV2Breakdown.
 */
import React from 'react';

function fmt(v, digits = 4) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return Number(v).toFixed(digits);
}

function fmtMult(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  if (n >= 10) return `×${n.toFixed(1)}`;
  return `×${n.toFixed(2)}`;
}

function ratioTone(ratio) {
  if (ratio == null || !Number.isFinite(Number(ratio))) return 'muted';
  const r = Number(ratio);
  if (r > 1.25) return 'hot'; // inflate
  if (r < 0.8) return 'cool'; // compress
  return 'ok';
}

/** Compact bar: expected at center (1.0); actual marker scales log-ish for readability. */
function RatioBar({ ratio }) {
  if (ratio == null || !Number.isFinite(Number(ratio)) || Number(ratio) <= 0) {
    return <div className="unc-ratio-bar unc-ratio-bar--empty" aria-hidden />;
  }
  const r = Number(ratio);
  // Map ratio to 5%–95% of track; 1.0 sits at 50%.
  const logPos = 0.5 + 0.45 * (Math.log(r) / Math.log(4)); // ×4 → ~95%, ÷4 → ~5%
  const pct = Math.max(5, Math.min(95, logPos * 100));
  return (
    <div className="unc-ratio-bar" title={`multiplier ${fmtMult(r)}`}>
      <div className="unc-ratio-track">
        <span className="unc-ratio-mid" />
        <span className="unc-ratio-marker" style={{ left: `${pct}%` }} />
      </div>
    </div>
  );
}

function CompareRow({
  name,
  hint,
  expected,
  actual,
  ratio,
  expectedLabel = 'expected',
  actualLabel = 'actual',
  omitted = false,
}) {
  const tone = omitted ? 'muted' : ratioTone(ratio);
  return (
    <div className={`unc-compare${omitted ? ' unc-compare--omitted' : ''}`}>
      <div className="unc-compare-head">
        <span className="unc-compare-name" title={hint}>
          {name}
        </span>
        <span className={`unc-compare-mult unc-compare-mult--${tone}`}>
          {omitted ? 'omitted' : fmtMult(ratio)}
        </span>
      </div>
      <div className="unc-compare-line">
        <div className="unc-compare-cell">
          <span className="unc-compare-k">{expectedLabel}</span>
          <span className="unc-compare-v">{expected}</span>
        </div>
        <span className="unc-compare-vs" aria-hidden>
          →
        </span>
        <div className="unc-compare-cell">
          <span className="unc-compare-k">{actualLabel}</span>
          <span className="unc-compare-v">{actual}</span>
        </div>
      </div>
      {!omitted ? <RatioBar ratio={ratio} /> : null}
    </div>
  );
}

function FactorRow({ name, hint, detail, mult }) {
  return (
    <div className="unc-compare">
      <div className="unc-compare-head">
        <span className="unc-compare-name" title={hint}>
          {name}
        </span>
        <span className={`unc-compare-mult unc-compare-mult--${ratioTone(mult)}`}>
          {fmtMult(mult)}
        </span>
      </div>
      <div className="unc-compare-line">
        <div className="unc-compare-cell">
          <span className="unc-compare-v">{detail}</span>
        </div>
      </div>
      <RatioBar ratio={mult} />
    </div>
  );
}

/**
 * kf.uncertainty_model v2: P_panel = baseline × activity + V_common, with
 * baseline = e^a · n2^−b · (1+n1)^−c · (h/(h+h0))^d.
 */
function PanelV2Breakdown({ quote, d }) {
  const k = d.coefficients || {};
  const n2 = Number(d.n2);
  const n1 = Number(d.n1);
  const h = d.hours_to_start;
  const level = Number.isFinite(Number(k.a)) ? Math.exp(Number(k.a)) : null;
  const booksMult =
    level != null && n2 > 0
      ? Math.exp(-Number(k.b) * Math.log(n2) - Number(k.c) * Math.log1p(Math.max(n1, 0)))
      : null;
  const timeMult =
    h != null && Number(h) > 0
      ? Math.pow(Number(h) / (Number(h) + Number(k.h0_hours)), Number(k.d))
      : null;
  const fuse = d.fuse || null;
  return (
    <div className="card unc-bd-card">
      <h3>Uncertainty sourcing (calibrated panel)</h3>
      <p className="unc-bd-lede">
        P_panel = baseline × activity + V_common: books and time to start set the baseline, recent
        quote moves scale it. With no sharp action, Quote P equals P_panel.
        {quote.quote_scenario_label || quote.quote_scenario
          ? ` · ${quote.quote_scenario_label || quote.quote_scenario}`
          : ''}
      </p>

      <FactorRow
        name="Level (market class)"
        hint="e^a, fitted per market class"
        detail={`e^a = ${fmt(level, 4)}`}
        mult={null}
      />
      <FactorRow
        name="Books quoting"
        hint="n2^−b · (1+n1)^−c"
        detail={`${n2} two-sided, ${n1} one-sided (b ${fmt(k.b, 2)}, c ${fmt(k.c, 2)})`}
        mult={booksMult}
      />
      <FactorRow
        name="Time to start"
        hint="(h / (h + h0))^d — tightens toward the start"
        detail={
          h != null
            ? `${Number(h).toFixed(1)}h to start (h0 ${fmt(k.h0_hours, 1)}h, d ${fmt(k.d, 2)})`
            : 'no start time'
        }
        mult={timeMult}
      />
      <FactorRow
        name="Recent movement"
        hint="1 + α·r^κ; r = |C_move − EMA_τ(C_move)| / √baseline"
        detail={`r = ${fmt(d.activity_r, 3)} (α ${fmt(k.alpha, 2)}, κ ${fmt(k.kappa, 2)}, τ ${fmt(
          k.tau_hours,
          1
        )}h)`}
        mult={d.activity_mult}
      />

      <div className="unc-result">
        <div className="unc-result-formula">
          <span className="unc-result-k">P_panel</span>
          <span className="unc-result-eq">
            = {fmt(d.baseline, 5)}
            <span className="unc-result-op">×</span>
            {fmtMult(d.activity_mult).replace('×', '')}
            <span className="unc-result-op">+</span>
            {fmt(d.v_common, 5)}
            <span className="unc-result-op">=</span>
            <strong>{fmt(d.P_panel, 5)}</strong>
          </span>
        </div>
        <div className="unc-result-meta">
          <span title="Baseline before activity">
            baseline <strong>{fmt(d.baseline, 5)}</strong>
          </span>
          <span title="Closing line's own error (provisional)">
            V_common <strong>{fmt(d.v_common, 5)}</strong>
          </span>
          <span title="Published posterior: P_panel plus whatever sharp action has tightened or offset">
            Quote P <strong>{fmt(quote.P, 5)}</strong>
          </span>
        </div>
      </div>

      <h4 className="unc-bd-sub">Last consensus update</h4>
      <div className="unc-result-meta">
        <span title="Consensus change from books quoting in both batches">
          ΔC move <strong>{fmt(d.dC_move, 4)}</strong>
        </span>
        <span title="Consensus change from books joining or leaving (no information, no gain)">
          ΔC composition <strong>{fmt(d.dC_comp, 4)}</strong>
        </span>
        <span title="Innovation gate statistic: ΔC_move² / baseline">
          z² (quote moves) <strong>{fmt(d.z2_move, 3)}</strong>
        </span>
      </div>
      <div className="unc-result-meta">
        <span title="Share of a sharp offset this move absorbed: γΔ² / (P_panel + γΔ²)">
          K <strong>{fuse ? fmt(fuse.K, 3) : '—'}</strong>
        </span>
        <span title="Sharp-absorption scale">
          γ <strong>{fmt(k.gamma, 2)}</strong>
        </span>
        <span title="P_panel,t / P_panel,t−1 applied to P before the move">
          P ratio <strong>{fuse ? fmtMult(fuse.ratio) : '—'}</strong>
        </span>
      </div>
    </div>
  );
}

export default function UncertaintyBreakdownPanel({ quote }) {
  if (quote && quote.uncertainty_detail && quote.uncertainty_detail.model === 'v2') {
    return <PanelV2Breakdown quote={quote} d={quote.uncertainty_detail} />;
  }
  if (!quote) {
    return (
      <div className="card unc-bd-card">
        <h3>Uncertainty</h3>
        <div className="empty-state compact">Select a market</div>
      </div>
    );
  }

  const r0 = quote.R0;
  const ed = quote.expected_disp;
  const ev = quote.expected_vig;
  const vDisp = quote.V_disp;
  const sPanel = quote.s_panel != null ? quote.s_panel : quote.vig_implied;
  const dispRatio =
    quote.disp_ratio != null
      ? Number(quote.disp_ratio)
      : ed != null && vDisp != null && Number(ed) > 0
        ? Number(vDisp) / Number(ed)
        : null;
  const vigRatio = quote.vig_ratio != null ? Number(quote.vig_ratio) : null;
  const applyVig = quote.apply_vig;
  const vigOmitted = applyVig === false;
  const rEff = quote.R_eff;
  const P = quote.P;
  const hasBreakdown =
    rEff != null || vDisp != null || r0 != null || (ed != null && ev != null);

  const product =
    r0 != null &&
    dispRatio != null &&
    Number.isFinite(Number(r0)) &&
    Number.isFinite(dispRatio)
      ? Number(r0) *
        dispRatio *
        (vigOmitted || vigRatio == null ? 1 : Number(vigRatio))
      : null;

  return (
    <div className="card unc-bd-card">
      <h3>Uncertainty sourcing</h3>
      <p className="unc-bd-lede">
        Each row: <strong>expected</strong> (config) vs <strong>actual</strong> (live panel). Multiplier
        &gt;1 widens R_eff; &lt;1 tightens it.
        {quote.quote_scenario_label || quote.quote_scenario
          ? ` · ${quote.quote_scenario_label || quote.quote_scenario}`
          : ''}
        {quote.scenario_n_two_sided != null
          ? ` · ${quote.scenario_n_two_sided} two-sided / ${quote.scenario_n_one_sided ?? 0} one-sided`
          : ''}
      </p>

      {!hasBreakdown ? (
        <div className="empty-state compact">
          No panel breakdown yet for this market (waiting on kf_diagnostics / uncertainty_params).
        </div>
      ) : (
        <>
          <CompareRow
            name="Book disagreement"
            hint="V_disp / expected_disp (logit²)"
            expected={fmt(ed, 4)}
            actual={fmt(vDisp, 4)}
            ratio={dispRatio}
            expectedLabel="expected_disp"
            actualLabel="V_disp"
          />

          <CompareRow
            name="Panel vig (hold)"
            hint="S_panel / expected_vig — omitted when panel is one-sided"
            expected={fmt(ev, 4)}
            actual={vigOmitted ? '—' : fmt(sPanel, 4)}
            ratio={vigOmitted ? null : vigRatio}
            expectedLabel="expected_vig"
            actualLabel="S_panel"
            omitted={vigOmitted}
          />

          {quote.missing_vig_imputed ? (
            <p className="unc-bd-note">
              Missing expected books padded into vig at{' '}
              {quote.missing_vig_value != null
                ? `${(Number(quote.missing_vig_value) * 100).toFixed(1)}%`
                : 'margin_ceiling'}
              .
            </p>
          ) : null}

          <div className="unc-result">
            <div className="unc-result-formula">
              <span className="unc-result-k">R_eff</span>
              <span className="unc-result-eq">
                = {fmt(r0, 4)}
                <span className="unc-result-op">×</span>
                {fmtMult(dispRatio).replace('×', '')}
                {!vigOmitted ? (
                  <>
                    <span className="unc-result-op">×</span>
                    {fmtMult(vigRatio).replace('×', '')}
                  </>
                ) : (
                  <span className="unc-result-skip"> × vig omitted</span>
                )}
                <span className="unc-result-op">=</span>
                <strong>{fmt(rEff != null ? rEff : product, 5)}</strong>
              </span>
            </div>
            <div className="unc-result-meta">
              <span title="Config base when both ratios ≈ 1">
                R0 <strong>{fmt(r0, 4)}</strong>
              </span>
              <span title="Published posterior (may include dropout scar above panel R_eff)">
                Quote P <strong>{fmt(P, 5)}</strong>
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
